export const formatTimestamp = (
  timestamp: string | null,
  userLocale: string,
  options: {
    includeTimezone?: boolean;
    onlyHourAndMinute?: boolean;
  } = {
    includeTimezone: false,
    onlyHourAndMinute: false
  }
): string => {
  if (!timestamp) {
    return 'Not Available'; // Fallback for missing dates
  }

  const date = new Date(timestamp);
  const formattedDate = date.toLocaleString(
    userLocale,
    options.onlyHourAndMinute
      ? {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true // 12-hour format for better readability
        }
      : {
          year: 'numeric',
          month: 'long', // Full month name for clarity
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true // 12-hour format for better readability
        }
  );

  if (options.includeTimezone) {
    const timezoneName = Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    }).formatToParts(date);

    const timeZonePart = timezoneName.find((part) => part.type === 'timeZoneName');
    const timezone = timeZonePart ? timeZonePart.value : '';

    return `${formattedDate} (${timezone})`; // Append timezone info
  }

  return formattedDate;
};

/**
 * Helper function to format duration parts using Intl.DurationFormat
 * @param hours - Hours component
 * @param minutes - Minutes component
 * @param seconds - Seconds component
 * @param locale - User's locale
 * @returns Formatted duration string
 */
const formatDurationParts = (hours: number, minutes: number, seconds: number, locale?: string): string => {
  // Check if Intl.DurationFormat is available (future browser support)
  if ('DurationFormat' in Intl) {
    const df = new (Intl as any).DurationFormat(locale || navigator.language, {
      style: 'digital'
    });
    return df.format({ hours, minutes, seconds });
  }

  // Fallback for browsers without Intl.DurationFormat support
  const numberFormat = new Intl.NumberFormat(locale || navigator.language, {
    minimumIntegerDigits: 2,
    useGrouping: false
  });

  return `${numberFormat.format(hours)}:${numberFormat.format(minutes)}:${numberFormat.format(seconds)}`;
};

/**
 * Converts milliseconds to localized duration format
 * @param milliseconds - Time in milliseconds
 * @param locale - User's locale (defaults to browser locale)
 * @returns Formatted duration string using Intl APIs
 */
export const millisecondsToHHMMSS = (milliseconds: number, locale?: string): string => {
  if (!milliseconds || milliseconds < 0) {
    return formatDurationParts(0, 0, 0, locale);
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return formatDurationParts(hours, minutes, seconds, locale);
};

/**
 * Converts seconds to localized duration format
 * @param seconds - Time in seconds
 * @param locale - User's locale (defaults to browser locale)
 * @returns Formatted duration string using Intl.DurationFormat
 */
export const secondsToHHMMSS = (seconds: number, locale?: string): string => {
  if (!seconds || seconds < 0) {
    return formatDurationParts(0, 0, 0, locale);
  }

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  return formatDurationParts(hours, minutes, remainingSeconds, locale);
};
